/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router-dom components
import { useNavigate, Link, useLocation } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import SSOButton from "components/atoms/Buttons/SSOButton";

import CircularProgress from "@mui/material/CircularProgress";

import useAuth from "auth/useAuth";
import CoverLayout from "pages/authentication/components/CoverLayout";
import logoImage from "assets/images/Alysio-logo-white.svg";
import { FormHelperText } from "@mui/material";

function Cover() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordErrorMessage, setPasswordErrorMessage] = useState("");
  const [showUniqueClientSSO, setShowUniqueClientSSO] = useState(false);
  const [uniqueClientSSOParams, setUniqueClientSSOParams] = useState();

  const { login, isLoading, error } = useAuth();

  const navigate = useNavigate();
  const { search } = useLocation();

  const signInHandler = async (e) => {
    e?.preventDefault?.();
    if (email && password) {
      setEmailError(false);
      setPasswordError(false);
      const dataToPass = { email, password };

      login(dataToPass, search);
    } else {
      setEmailError(true);
      setPasswordError(true);
      setEmailErrorMessage("Email field is required!");
      setPasswordErrorMessage("Password field is required!");
    }
  };

  const emailHandler = async (e) => {
    const { value } = e.target;
    if (value !== "") {
      if (
        value
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          )
      ) {
        setEmail(value);
        setEmailError(false);
      } else {
        setEmailError(true);
        setEmailErrorMessage("Email format is not proper!");
      }
    } else {
      setEmailError(true);
      setEmailErrorMessage("Email field is required!");
    }
  };

  const passwordHandler = async (e) => {
    const { value } = e.target;

    if (value !== "") {
      if (
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[$*.[\]{}()?\-"!@#%&,><':;|_~`+=/\\^])[A-Za-z\d^$*.[$*.[\]{}()?\-"!@#%&,><':;|_~`+=/\\^]{8,}$/.test(
          value
        )
      ) {
        setPassword(value);
        setPasswordError(false);
      } else {
        setPasswordError(true);
        setPasswordErrorMessage(
          "Password must be at least 8 characters and contain at least one lowercase letter, one uppercase letter, one number, and one special character."
        );
      }
    } else {
      setPasswordError(true);
      setPasswordErrorMessage("Password field is required!");
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(search || "");
    const ssoProvider = searchParams.get("sso");
    const clientId = searchParams.get("clientId");

    if (ssoProvider && clientId) {
      setUniqueClientSSOParams({
        provider: ssoProvider,
        clientId,
      });
      setShowUniqueClientSSO(true);
    }
  }, []);

  return (
    <CoverLayout>
      {isLoading ? (
        <Stack alignItems="center">
          <CircularProgress
            sx={{
              position: "fixed",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 2,
            }}
            color="info"
            size="10vh"
          />
        </Stack>
      ) : (
        ""
      )}
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="secondary"
          mx={2}
          mt={-3}
          p={3}
          mb={1}
          textAlign="center"
        >
          <MDBox sx={{ display: "flex", justifyContent: "center", padding: ".5rem" }}>
            <MDBox component="img" src={logoImage} alt="Brand" width="90%" />
          </MDBox>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox
            component="form"
            role="form"
            sx={{
              "& .MuiFormHelperText-root": { color: "#d32f2f" },
              "& .MuiInputLabel-formControl": { lineHeight: "20px !important" },
            }}
            onSubmit={signInHandler}
          >
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                variant="standard"
                fullWidth
                onChange={(e) => emailHandler(e)}
                error={emailError}
                helperText={emailError ? emailErrorMessage : ""}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                variant="standard"
                fullWidth
                onChange={(e) => passwordHandler(e)}
                error={passwordError}
              />
            </MDBox>
            <FormHelperText style={{ marginTop: "-15px" }}>
              {passwordError ? passwordErrorMessage : ""}
            </FormHelperText>
            <MDBox>
              <span style={{ color: "#d32f2f", fontSize: "12px" }}>{error}</span>
            </MDBox>

            <MDTypography variant="button" color="info" to="/forgot-password" component={Link}>
              Forgot Password?
            </MDTypography>

            <MDBox mt={4} mb={1}>
              <MDButton color="primaryRed" fullWidth type="submit" onClick={signInHandler}>
                log in
              </MDButton>
            </MDBox>
            {showUniqueClientSSO && (
              <MDBox mt={4} mb={1}>
                <SSOButton
                  provider={uniqueClientSSOParams?.provider}
                  clientId={uniqueClientSSOParams?.clientId}
                />
              </MDBox>
            )}
            <MDBox mt={4} mb={1}>
              <SSOButton provider="google" />
            </MDBox>

            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  onClick={() => {
                    navigate("/sign-up", { replace: true });
                  }}
                  style={{ cursor: "pointer" }}
                  variant="button"
                  color="secondaryBlue"
                  fontWeight="medium"
                  fontSize=".8 rem"
                >
                  Sign Up
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </CoverLayout>
  );
}

export default Cover;
